import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { ToastContext, type ToastContextValue, type ToastVariant } from './ToastContext';

interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  durationMs: number;
}

const VARIANT_STYLES: Record<ToastVariant, { icon: typeof CheckCircle2; cls: string }> = {
  success: { icon: CheckCircle2, cls: 'bg-kw-emerald-light text-kw-tag-text border-kw-tag-text/20' },
  warning: { icon: AlertTriangle, cls: 'bg-kw-warning-bg text-kw-warning-text border-kw-warning-text/20' },
  danger: { icon: XCircle, cls: 'bg-kw-danger-bg text-kw-danger-text border-kw-danger-text/20' },
  info: { icon: Info, cls: 'bg-kw-cerulean-light text-kw-cerulean-dark border-kw-cerulean-dark/20' },
};

/**
 * Mounts a viewport for transient notifications and exposes `show()` via the
 * ToastContext. Use the {@link useToast} hook to trigger toasts from anywhere
 * inside the provider tree.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeouts = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeouts.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeouts.current.delete(id);
    }
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (message, options) => {
      const id = `toast-${String(Date.now())}-${Math.random().toString(36).slice(2, 7)}`;
      const toast: Toast = {
        id,
        message,
        variant: options?.variant ?? 'info',
        durationMs: options?.durationMs ?? 4000,
      };
      setToasts((prev) => [...prev, toast]);
      const timeout = setTimeout(() => {
        dismiss(id);
      }, toast.durationMs);
      timeouts.current.set(id, timeout);
    },
    [dismiss],
  );

  // Cleanup all pending timeouts on unmount
  useEffect(() => {
    const map = timeouts.current;
    return () => {
      map.forEach((t) => {
        clearTimeout(t);
      });
      map.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {/* Toast viewport — bottom on mobile, top-right on desktop. Sits above bottom nav. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(72px+var(--safe-area-inset-bottom))] z-50 flex flex-col items-center gap-2 px-4 sm:bottom-auto sm:right-4 sm:top-4 sm:items-end sm:px-0">
        {toasts.map((toast) => {
          const { icon: Icon, cls } = VARIANT_STYLES[toast.variant];
          return (
            <div
              key={toast.id}
              role="status"
              className={cn(
                'pointer-events-auto flex w-full max-w-sm animate-slide-up items-start gap-3 rounded-kw-lg border bg-white px-4 py-3 shadow-kw-md',
                cls,
              )}
            >
              <Icon className="mt-0.5 size-5 shrink-0" />
              <p className="flex-1 text-[13px]">{toast.message}</p>
              <button
                type="button"
                onClick={() => {
                  dismiss(toast.id);
                }}
                aria-label="Dismiss"
                className="text-current/60 hover:text-current"
              >
                <X className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
